import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Illustration } from '../components/Illustration';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { getBackendUrl } from '../lib/api';
import { useRegion } from '../hooks/useRegion';
import { getPhonePlaceholder } from '../lib/regionConfig';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Loader2, Pencil, Trash2, Users, Mail, Phone, MapPin, X, UserPlus, FileSpreadsheet, Upload, Download, Database, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../components/PageHeader';
import { ClientMark } from '../components/ClientMark';
import { PhoneList, PhoneListRow } from '../components/PhoneList';
import { PhonePanel } from '../components/PhonePanel';
import { useIsPhone } from '../hooks/useIsPhone';
import { cn } from '../lib/utils';
import { PageToolbar, STICKY_TABLE, stickyTableHeader } from '../components/PageToolbar';
import FileDropzone from '@/components/FileDropzone';

export default function ClientsPage() {
  const { clients, clientsLoading, fetchClients, createClient, updateClient, deleteClient, token } = useAppStore();
  const { region } = useRegion();
  const isPhone = useIsPhone();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDialog, setShowDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Import/Export state
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState({ clients: [], contactCount: 0 });
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [formData, setFormData] = useState({
    business_name: '',
    billing_address: '',
    contacts: [],
  });

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Handle edit query parameter from search navigation
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && clients.length > 0) {
      const clientToEdit = clients.find(c => c.id === editId);
      if (clientToEdit) {
        setFormData({
          business_name: clientToEdit.business_name || '',
          billing_address: clientToEdit.billing_address || '',
          contacts: clientToEdit.contacts || [],
        });
        setEditingClient(clientToEdit);
        setShowDialog(true);
        // Clear the query param after opening
        setSearchParams({});
      }
    }
  }, [searchParams, clients, setSearchParams]);

  const resetForm = () => {
    setFormData({
      business_name: '',
      billing_address: '',
      contacts: [],
    });
    setEditingClient(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      business_name: client.business_name || '',
      billing_address: client.billing_address || '',
      contacts: client.contacts || [],
    });
    setShowDialog(true);
  };

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [...formData.contacts, { name: '', email: '', phone: '' }],
    });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...formData.contacts];
    newContacts[index][field] = value;
    setFormData({ ...formData, contacts: newContacts });
  };

  const removeContact = (index) => {
    const newContacts = formData.contacts.filter((_, i) => i !== index);
    setFormData({ ...formData, contacts: newContacts });
  };

  const handleSubmit = async () => {
    if (!formData.business_name.trim()) {
      toast.error('Business name is required');
      return;
    }

    // Validate contacts - each contact must have an email
    for (let i = 0; i < formData.contacts.length; i++) {
      if (!formData.contacts[i].email || !formData.contacts[i].email.trim()) {
        toast.error(`Contact ${i + 1}: Email is required`);
        return;
      }
    }

    setLoading(true);
    try {
      if (editingClient) {
        await updateClient(editingClient.id, formData);
        toast.success('Client updated');
      } else {
        await createClient(formData);
        toast.success('Client created');
      }
      setShowDialog(false);
      resetForm();
    } catch (error) {
      toast.error(editingClient ? 'Failed to update client' : 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await deleteClient(clientId);
      toast.success('Client deleted');
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  // ============== CSV Import/Export Functions ==============
  
  // Parse CSV line handling quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };
  
  // Download Full CSV Template
  const handleDownloadTemplate = () => {
    const rows = [
      ['business_name', 'billing_address', 'contact_name', 'contact_email', 'contact_phone'],
      ['ABC Productions', '123 Main St, Sydney NSW 2000', 'John Doe', 'john@abc.com', '0400-123-456'],
      ['', '', 'Jane Smith', 'jane@abc.com', '0400-789-012'],
      ['XYZ Studio', '456 Oak Ave, Melbourne VIC 3000', 'Mike Brown', 'mike@xyz.com', ''],
      ['Solo Operator', '', 'Sam Wilson', 'sam@solo.com', '0412-345-678'],
    ];
    
    const csvContent = rows.map(row => 
      row.map(cell => {
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'clients_import_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };
  
  // Export Full CSV
  const handleExportCSV = async () => {
    setExporting(true);
    const toastId = toast.loading('Exporting clients...');
    
    try {
      const API_URL = getBackendUrl();
      const response = await fetch(`${API_URL}/api/clients/export-full-csv`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || 'clients_export.csv';
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded!', { id: toastId });
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export', { id: toastId });
    } finally {
      setExporting(false);
    }
  };
  
  // Handle file selection for import
  const handleFileSelect = (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImportFile(file);
    
    // Preview the CSV
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        toast.error('CSV file must have headers and at least one data row');
        setImportFile(null);
        return;
      }
      
      const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
      
      // Check for required header
      if (!headers.includes('business_name')) {
        toast.error('Missing required column: business_name');
        setImportFile(null);
        return;
      }
      
      // Parse and preview
      const preview = { clients: [], contactCount: 0 };
      let currentClient = null;
      
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        
        if (row.business_name && row.business_name.trim()) {
          if (currentClient) {
            preview.clients.push(currentClient);
          }
          currentClient = {
            name: row.business_name.trim(),
            address: row.billing_address || '',
            contactCount: 0
          };
          
          if (row.contact_email && row.contact_email.trim()) {
            currentClient.contactCount++;
            preview.contactCount++;
          }
        } else if (currentClient && row.contact_email && row.contact_email.trim()) {
          currentClient.contactCount++;
          preview.contactCount++;
        }
      }
      
      if (currentClient) {
        preview.clients.push(currentClient);
      }
      
      setImportPreview(preview);
    };
    reader.readAsText(file);
  };
  
  // Import CSV
  const handleImportCSV = async () => {
    if (!importFile) return;
    
    setImporting(true);
    const toastId = toast.loading('Importing clients...');
    
    try {
      const text = await importFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
      
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        rows.push(row);
      }
      
      const API_URL = getBackendUrl();
      const response = await fetch(`${API_URL}/api/clients/import-full-csv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rows })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Import failed');
      }
      
      const result = await response.json();
      toast.success(result.message, { id: toastId });
      
      // Refresh clients list
      fetchClients();
      
      // Close dialog and reset
      setIsImportDialogOpen(false);
      setImportFile(null);
      setImportPreview({ clients: [], contactCount: 0 });
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.message || 'Failed to import', { id: toastId });
    } finally {
      setImporting(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const searchLower = searchTerm.toLowerCase();
    const businessMatch = client.business_name?.toLowerCase().includes(searchLower);
    const contactMatch = client.contacts?.some(c => 
      c.email?.toLowerCase().includes(searchLower) ||
      c.name?.toLowerCase().includes(searchLower)
    );
    return businessMatch || contactMatch;
  });

  return (
    <Layout>
      <div className="space-y-8 animate-in" data-testid="clients-page">
        {/* Header */}
        <PageHeader title="Clients" />

        {/* Search with Action Buttons */}
        {isPhone ? (
          <PhonePanel
            actions={[{ label: 'Add client', icon: Plus, onClick: openNewDialog, primary: true, testId: 'add-client-btn' }]}
          />
        ) : (
        <PageToolbar hairline={false} className="sm:flex-row sm:items-center sm:justify-between">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-muted-foreground pointer-events-none" />
            <Input
              aria-label="Search clients"
              placeholder="Search clients"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-11 w-full sm:w-[320px]"
              data-testid="client-search"
            />
          </div>
          <div className="flex items-center gap-2">
            {/* Import/Export Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="hidden sm:flex">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Import / Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setIsImportDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportCSV} disabled={exporting || clients.length === 0}>
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export CSV
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleDownloadTemplate}>
                  <Database className="w-4 h-4 mr-2" />
                  Download Template
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button onClick={openNewDialog} data-testid="add-client-btn">
              <Plus className="w-4 h-4" /> Add client
            </Button>
          </div>
        </PageToolbar>
        )}

        {/* Clients List */}
        {clientsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredClients.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              {searchTerm
                ? <Users className="w-12 h-12 text-muted-foreground mb-4" />
                : <Illustration name="clients" className="w-full max-w-xs mb-2" />}
              <h3 className="text-lg font-medium mb-2">
                {searchTerm ? 'No clients found' : 'No clients yet'}
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                {searchTerm 
                  ? 'Try adjusting your search' 
                  : 'Add your first client to get started'
                }
              </p>
              {!searchTerm && (
                <Button onClick={openNewDialog}>
                  <Plus className="w-4 h-4" /> Add client
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
          <PhoneList data-testid="clients-phone-list">
            {filteredClients.map((client) => {
              const contactCount = client.contacts?.length || 0;
              const email = client.contacts?.[0]?.email;
              return (
                <PhoneListRow
                  key={client.id}
                  onClick={() => openEditDialog(client)}
                  leading={<ClientMark name={client.business_name} />}
                  title={client.business_name}
                  detail={<span className="truncate">{email || `${contactCount} ${contactCount === 1 ? 'contact' : 'contacts'}`}</span>}
                  data-testid={`client-phone-row-${client.id}`}
                />
              );
            })}
          </PhoneList>
          <Card className={cn(STICKY_TABLE.card, 'max-sm:hidden')}>
            <CardContent className="p-0">
              <Table containerClassName={STICKY_TABLE.container}>
                <TableHeader {...stickyTableHeader()}>
                  <TableRow>
                    <TableHead className="py-3 px-4">Business name</TableHead>
                    <TableHead className="hidden md:table-cell py-3 px-4">Email</TableHead>
                    <TableHead className="hidden xl:table-cell py-3 px-4">Address</TableHead>
                    <TableHead className="w-[100px] py-3 px-4">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => {
                    const firstContact = client.contacts?.[0];
                    const contactCount = client.contacts?.length || 0;
                    return (
                      <TableRow 
                        key={client.id} 
                        data-testid={`client-row-${client.id}`}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => openEditDialog(client)}
                      >
                        <TableCell className="py-3 px-4">
                          <div className="flex items-start gap-3">
                          <ClientMark name={client.business_name} className="mt-0.5" />
                          <div className="min-w-0">
                            <p className="font-medium">{client.business_name}</p>
                            {contactCount > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {contactCount} {contactCount === 1 ? 'contact' : 'contacts'}
                              </span>
                            )}
                            <div className="md:hidden text-sm text-muted-foreground mt-1">
                              {firstContact?.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {firstContact.email}
                                </span>
                              )}
                            </div>
                          </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell py-3 px-4">
                          {firstContact?.email && (
                            <span className="flex items-center gap-1 text-sm">
                              <Mail className="w-3 h-3 text-muted-foreground" /> {firstContact.email}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell py-3 px-4">
                          {client.billing_address && (
                            <span className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="w-3 h-3" /> {client.billing_address}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="py-3 px-4">
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(client)}
                              data-testid={`edit-client-${client.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(client.id)}
                              className="text-muted-foreground hover:text-destructive"
                              data-testid={`delete-client-${client.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          </>
        )}
      </div>

      {/* Client Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edit client' : 'Add client'}</DialogTitle>
            <DialogDescription>
              {editingClient ? 'Update client information' : 'Add a new client to your database'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="business_name">Business name *</Label>
              <Input
                id="business_name"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                placeholder="Client business name"
                data-testid="client-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="billing_address">Billing address</Label>
              <Input
                id="billing_address"
                value={formData.billing_address}
                onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                placeholder="123 Business St, Sydney NSW 2000"
                data-testid="client-address-input"
              />
            </div>

            {/* Contacts Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Contacts</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addContact}
                  data-testid="add-contact-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add contact
                </Button>
              </div>

              {formData.contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-lg">
                  No contacts yet. Click &quot;Add contact&quot; to add one.
                </p>
              ) : (
                <div className="space-y-3">
                  {formData.contacts.map((contact, index) => (
                    <Card key={index} className="p-3">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Contact {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => removeContact(index)}
                            data-testid={`remove-contact-${index}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-xs">Name (Optional)</Label>
                          <Input
                            placeholder="Contact name"
                            value={contact.name || ''}
                            onChange={(e) => updateContact(index, 'name', e.target.value)}
                            data-testid={`contact-name-${index}`}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Email *</Label>
                          <Input
                            type="email"
                            placeholder="contact@example.com"
                            value={contact.email}
                            onChange={(e) => updateContact(index, 'email', e.target.value)}
                            data-testid={`contact-email-${index}`}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Phone (Optional)</Label>
                          <Input
                            placeholder={getPhonePlaceholder(region)}
                            value={contact.phone || ''}
                            onChange={(e) => updateContact(index, 'phone', e.target.value)}
                            data-testid={`contact-phone-${index}`}
                          />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingClient ? 'Save changes' : 'Create client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Import CSV Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={(open) => {
        setIsImportDialogOpen(open);
        if (!open) {
          setImportFile(null);
          setImportPreview({ clients: [], contactCount: 0 });
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Clients</DialogTitle>
            <DialogDescription>
              Import clients and their contacts from a CSV file.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Download Template */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="text-sm font-medium">CSV Template</p>
                <p className="text-xs text-muted-foreground">Download template with example data</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
            </div>
            
            {/* Format info */}
            <div className="text-xs text-muted-foreground space-y-2 p-3 bg-muted/50 rounded-lg">
              <p><strong>How it works:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Row with <code className="bg-muted px-1 rounded">business_name</code> starts a new client</li>
                <li>Subsequent rows with empty business_name add contacts to that client</li>
                <li>Each contact must have an email address</li>
              </ul>
              <p className="mt-2"><strong>Columns:</strong> business_name, billing_address, contact_name, contact_email, contact_phone</p>
            </div>
            
            {/* File Upload */}
            <FileDropzone accept=".csv" onSelect={handleFileSelect} title="Choose a CSV file" />
            
            {/* Preview */}
            {importPreview.clients && importPreview.clients.length > 0 && (
              <div className="space-y-2">
                <Label>Preview ({importPreview.clients.length} clients, {importPreview.contactCount} contacts)</Label>
                <div className="max-h-[200px] overflow-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Business name</TableHead>
                        <TableHead className="text-xs">Address</TableHead>
                        <TableHead className="text-xs text-right">Contacts</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importPreview.clients.slice(0, 10).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm py-2">{item.name}</TableCell>
                          <TableCell className="text-sm py-2 text-muted-foreground">
                            {item.address || '-'}
                          </TableCell>
                          <TableCell className="text-sm py-2 text-right">
                            {item.contactCount > 0 ? (
                              <Badge variant="secondary">{item.contactCount}</Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {importPreview.clients.length > 10 && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-2">
                            ... and {importPreview.clients.length - 10} more clients
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            {/* Import Button */}
            <Button 
              onClick={handleImportCSV} 
              disabled={!importFile || importing}
              className="w-full"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Clients
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
